/**
 * PitchTracker — YIN-based fundamental-frequency tracker over an AudioBuffer.
 *
 * Used by the Pitch tool's auto-tune feature to detect note boundaries
 * before applying scale-snap correction. Pure JS, no model fetch — the
 * YIN algorithm is the same one running inside chiptune-worklet.js
 * with offline batching on top.
 *
 * API:
 *   window.PitchTracker.track(audioBuffer, opts) → {
 *       hopSec: 0.010,
 *       frames: [{ timeS, freqHz, confidence }],
 *       segments: [{ startS, endS, medianFreqHz, frames: N }]
 *   }
 *
 * opts:
 *   hopSec      — analysis hop (default 0.010 = 10 ms)
 *   windowSec   — frame size (default 0.040 = 40 ms; YIN needs ≥2 periods
 *                 of the lowest expected pitch, so 40 ms covers down to 50 Hz)
 *   minFreqHz   — clamp on detection range (default 65 Hz, low E2)
 *   maxFreqHz   — clamp on detection range (default 1500 Hz, vocal high)
 *   minConf     — drop frames below this YIN confidence (default 0.85)
 *   minSegmentMs — minimum note length in ms (default 80 ms)
 *
 * Memory: O(N) — one Float32Array allocated per frame for the YIN buffer,
 * plus the frames array. ~3 MB for a 5-min file at 10 ms hop.
 *
 * Performance: YIN is O(W²) per frame (W = window samples). For 40 ms at
 * 44.1 kHz that's ~3 M ops per frame; ~30 K frames in a 5-min file →
 * ~90 G ops total → ~5-10 s on a midrange laptop. We yield to the event
 * loop every 200 frames so the UI stays responsive even without a worker.
 */
(function(){
'use strict';

function yieldFrame(){ return new Promise(function(r){ setTimeout(r, 0); }); }

// Compute YIN cumulative-mean-normalized-difference for a single frame.
// Returns { lag, conf } — lag is the period in samples, conf ∈ [0,1] (1=perfect).
// Returns lag=0 when no pitch detected above threshold.
// cmndThreshold: CMND dip depth required to call a frame voiced. 0.15 =
// clean instrument tones (default, original behavior). Separated vocal
// stems (reverb + separation artifacts) rarely dip that deep on ~half
// their genuinely sung frames — the caller can widen to ~0.45 and do the
// real voicing decision downstream on the confidence values (measured on
// a real stem 2026-07-16: raw voiced frames 33% → 64%, which doubled the
// note recall of the vocal segmenter in audio-to-notes-clean.js).
function yinFrame(buf, sampleRate, minLag, maxLag, cmndThreshold){
    var W = buf.length;
    var halfW = W >> 1;
    if (maxLag > halfW) maxLag = halfW;
    if (minLag < 1) minLag = 1;
    if (maxLag <= minLag) return { lag: 0, conf: 0 };

    // Step 1+2: difference function + CMND (fused, running sum).
    var THRESHOLD = cmndThreshold || 0.15;
    var runningSum = 0;
    var prevCmnd = 1;   // cmnd[tau-1] — kept for the parabola's left sample
    for (var tau = 1; tau <= maxLag; tau++) {
        var d = 0;
        for (var j = 0; j < halfW; j++) {
            var diff = buf[j] - buf[j + tau];
            d += diff * diff;
        }
        runningSum += d;
        var cmnd = runningSum === 0 ? 1 : d * tau / runningSum;
        // Track the first dip below threshold; if found, descend to its
        // local minimum then stop (early-exit saves the rest of the loop).
        if (tau >= minLag && cmnd < THRESHOLD) {
            // Descend to the local minimum, keeping the three CMND values
            // around it (yPrev, yMin, yNext) for parabolic interpolation.
            var yPrev = prevCmnd, yMin = cmnd, yNext = -1, bestLag = tau;
            while (tau + 1 <= maxLag) {
                var t1 = tau + 1;
                var d1 = 0;
                for (var k = 0; k < halfW; k++) {
                    var diff1 = buf[k] - buf[k + t1];
                    d1 += diff1 * diff1;
                }
                runningSum += d1;
                var cmnd1 = runningSum === 0 ? 1 : d1 * t1 / runningSum;
                if (cmnd1 < yMin) { yPrev = yMin; yMin = cmnd1; bestLag = t1; tau = t1; }
                else { yNext = cmnd1; break; }
            }
            // Step 5 — parabolic interpolation. The integer lag quantizes the
            // period ever more coarsely as pitch rises (≈+23 cents at E6, worse
            // above), which lands high guitar/instrument notes on the WRONG
            // pitch. Refining the minimum to a sub-sample lag fixes that, and
            // since it never changes WHICH frames are voiced (conf = 1 - yMin,
            // unchanged) it adds no false positives — accuracy-only, no downside.
            var lag = bestLag;
            if (yNext >= 0) {
                var denom = yPrev + yNext - 2 * yMin;
                if (denom > 0) {
                    var shift = 0.5 * (yPrev - yNext) / denom;
                    if (shift > -1 && shift < 1) lag = bestLag + shift;
                }
            }
            return { lag: lag, conf: Math.max(0, Math.min(1, 1 - yMin)) };
        }
        prevCmnd = cmnd;
    }
    return { lag: 0, conf: 0 };
}

async function track(audioBuffer, opts){
    opts = opts || {};
    var hopSec      = opts.hopSec      || 0.010;
    var windowSec   = opts.windowSec   || 0.040;
    var minFreqHz   = opts.minFreqHz   || 65;
    var maxFreqHz   = opts.maxFreqHz   || 1500;
    var minConf     = opts.minConf     || 0.85;
    var minSegMs    = opts.minSegmentMs || 80;
    var cmndTh      = opts.cmndThreshold || 0.15; // see yinFrame
    var onProgress  = typeof opts.onProgress === 'function' ? opts.onProgress : null;

    var sr = audioBuffer.sampleRate;
    var len = audioBuffer.length;

    // Use channel 0 (or mono mix) — pitch is single-track even on stereo.
    var ch0 = audioBuffer.getChannelData(0);
    var src = ch0;
    if (audioBuffer.numberOfChannels > 1) {
        var ch1 = audioBuffer.getChannelData(1);
        src = new Float32Array(len);
        for (var i = 0; i < len; i++) src[i] = (ch0[i] + ch1[i]) * 0.5;
    }

    var winSamples = Math.round(windowSec * sr);
    if ((winSamples & 1) === 1) winSamples++; // even
    var hopSamples = Math.max(1, Math.round(hopSec * sr));
    var minLag = Math.max(1, Math.floor(sr / maxFreqHz));
    var maxLag = Math.min(winSamples >> 1, Math.ceil(sr / minFreqHz));

    var frameBuf = new Float32Array(winSamples);
    var frames = [];
    var totalFrames = Math.floor((len - winSamples) / hopSamples) + 1;
    if (totalFrames < 1) {
        return { hopSec: hopSec, frames: [], segments: [] };
    }

    for (var f = 0; f < totalFrames; f++) {
        var off = f * hopSamples;
        for (var i2 = 0; i2 < winSamples; i2++) frameBuf[i2] = src[off + i2];
        var r = yinFrame(frameBuf, sr, minLag, maxLag, cmndTh);
        var freqHz = 0, conf = 0;
        if (r.lag > 0) {
            freqHz = sr / r.lag;
            conf = r.conf;
            if (freqHz < minFreqHz || freqHz > maxFreqHz) { freqHz = 0; conf = 0; }
        }
        frames.push({ timeS: off / sr, freqHz: freqHz, confidence: conf });
        if (onProgress && (f & 0xFF) === 0) onProgress(f / totalFrames);
        if ((f % 200) === 199) await yieldFrame();
    }
    if (onProgress) onProgress(1);

    // ── Segment detection ──
    // Walk frames in order, group consecutive voiced frames whose pitch
    // sits within ±50 cents of the running median into a single "note
    // segment". Reject segments shorter than minSegMs as noise / tracker
    // jitter — they would produce zero-length pitch corrections after
    // the stretch engine's start-pad eats the head.
    var segments = [];
    var minSegSec = minSegMs / 1000;
    var curStart = -1;
    var curFreqs = [];
    function flushSegment(endIdx){
        if (curStart < 0) return;
        var endS = frames[endIdx - 1].timeS;
        var dur = endS - curStart;
        if (dur >= minSegSec && curFreqs.length > 0) {
            var sorted = curFreqs.slice().sort(function(a,b){ return a - b; });
            var med = sorted[sorted.length >> 1];
            segments.push({ startS: curStart, endS: endS, medianFreqHz: med, frames: curFreqs.length });
        }
        curStart = -1;
        curFreqs = [];
    }
    var lastMed = 0;
    for (var fi = 0; fi < frames.length; fi++) {
        var fr = frames[fi];
        if (fr.freqHz === 0 || fr.confidence < minConf) {
            flushSegment(fi);
            lastMed = 0;
            continue;
        }
        if (curStart < 0) {
            curStart = fr.timeS;
            curFreqs = [fr.freqHz];
            lastMed = fr.freqHz;
            continue;
        }
        // Pitch jump > 50 cents → new segment.
        var cents = 1200 * Math.log2(fr.freqHz / lastMed);
        if (Math.abs(cents) > 50) {
            flushSegment(fi);
            curStart = fr.timeS;
            curFreqs = [fr.freqHz];
            lastMed = fr.freqHz;
            continue;
        }
        curFreqs.push(fr.freqHz);
        // Update running median (cheap approximation: every 4th frame).
        if ((curFreqs.length & 3) === 0) {
            var s2 = curFreqs.slice().sort(function(a,b){ return a - b; });
            lastMed = s2[s2.length >> 1];
        }
    }
    flushSegment(frames.length);

    return { hopSec: hopSec, frames: frames, segments: segments };
}

window.PitchTracker = {
    track: track,
    version: '1.0.0',
    engine: 'YIN (Cheveigne 2002), CMND threshold 0.15'
};

})();
